import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import {
  BREAKPOINT_WIDTH,
  CRMFilterOverride,
  CrmFiltersService,
  CrmInjectionToken,
  CrmObjectDependencies,
  ObjectType,
} from '@galaxy/crm/static';
import {
  CRMFieldSchemaApiService,
  FieldSchema,
  PagedRequestOptionsInterface,
  ScoringApiService,
  ScoringDefinitionInterface,
} from '@vendasta/crm';
import { GalaxyFilterDefinitionInterface } from '@vendasta/galaxy/filter/chips';
import { Observable, catchError, distinctUntilChanged, map, of, shareReplay, switchMap } from 'rxjs';
import { isAcceptedObjectType } from '../validators';
@Injectable()
export class ScoringDefinitionService {
  private readonly config = inject(CrmInjectionToken);
  private readonly fieldSchemaService = inject(CRMFieldSchemaApiService);
  private readonly crmFiltersService = inject(CrmFiltersService);
  private readonly scoringService = inject(ScoringApiService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly MOBILE_BREAKPOINT = `(max-width: ${BREAKPOINT_WIDTH.MOBILE})`;
  readonly isMobile$: Observable<boolean> = this.breakpointObserver.observe([this.MOBILE_BREAKPOINT]).pipe(
    map((result) => result.matches),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly fieldSchemas = new Map<ObjectType, Observable<FieldSchema[]>>();
  private readonly filters = new Map<ObjectType, Observable<GalaxyFilterDefinitionInterface[]>>();

  getFilterOverride(fieldId: string, objectType: ObjectType): Observable<CRMFilterOverride | undefined> {
    const objectDependencies = this.objectDependecies(objectType);
    if (!objectDependencies?.filterInputOverrides$) {
      return of(undefined);
    }
    return objectDependencies.filterInputOverrides$.pipe(
      map((overrides) => overrides.find((override) => override.fieldId === fieldId)),
    );
  }

  getFieldSchema$(crmObjectType: ObjectType): Observable<FieldSchema[]> {
    if (this.fieldSchemas.has(crmObjectType)) {
      return this.fieldSchemas.get(crmObjectType)!;
    }

    const pagingOptions: PagedRequestOptionsInterface = { cursor: '', pageSize: 200 };
    const fs = this.config.namespace$.pipe(
      switchMap((namespace) => this.fieldSchemaService.listFieldSchema({ namespace, crmObjectType, pagingOptions })),
      map((response) => response?.fieldSchemas),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.fieldSchemas.set(crmObjectType, fs);
    return fs;
  }

  getObjectFilters$(crmObjectType?: ObjectType): Observable<GalaxyFilterDefinitionInterface[]> {
    if (!isAcceptedObjectType(crmObjectType)) {
      return of([]);
    }

    if (this.filters.has(crmObjectType)) {
      return this.filters.get(crmObjectType)!;
    }

    const filters = this.crmFiltersService
      .listObjectFilters$('', crmObjectType)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.filters.set(crmObjectType, filters);
    return filters;
  }

  listScoringDefinitions(namespace: string, fieldId: string): Observable<ScoringDefinitionInterface[]> {
    return this.scoringService
      .listScoringDefinitions({ namespace, fieldId })
      .pipe(map((response) => response?.scoringDefinitions ?? []));
  }

  updateScoringDefinitions(
    namespace: string,
    fieldId: string,
    scoringDefinitions: ScoringDefinitionInterface[],
  ): Observable<boolean> {
    return this.scoringService.setScoringDefinitionsForFieldId({ namespace, fieldId, scoringDefinitions }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private objectDependecies(objectType: ObjectType): CrmObjectDependencies | undefined {
    switch (objectType) {
      case 'Company':
        return this.config.company;
      case 'Contact':
        return this.config.contact;
      default:
        return undefined;
    }
  }
}
